import { normalizePath } from '@policyfly/utils/string'
import set from 'lodash-es/set'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useApiStore } from './api'
import { hotReloadStore } from '@/utils/build'
import { removeInvalidDeletedDiffs } from '@/utils/diffHelpers'

import type { APIPayloadResponseWithUUID, NestedResponseObject } from '@/lib/Payload'
import type { ApplicationFormResponse } from '@policyfly/protobuf'
import type { PrimitiveValue } from '@policyfly/types/common'
import type { NamedDriversDiff, VehiclesDiff, PlatesDiff } from 'types/programData/apd'
import type { ComputedRef, Ref } from 'vue'

export interface DiffData {
  added: APIPayloadResponseWithUUID[]
  changed: { uuid4: string, changed: { k: [string, string], v: [PrimitiveValue, PrimitiveValue] } }[]
  removed: APIPayloadResponseWithUUID[]
}

export interface FormDataDiffData {
  added: { k: string, v: ApplicationFormResponse }[]
  changed: { k: string, v: [ApplicationFormResponse, ApplicationFormResponse] }[]
  removed: { k: string, v: ApplicationFormResponse }[]
}

export interface DiffMap<T extends PrimitiveValue = PrimitiveValue> {
  added: Record<string, APIPayloadResponseWithUUID<T>>
  changed: Record<string, { uuid4: string, v: [T, T] }>
  removed: Record<string, APIPayloadResponseWithUUID<T>>
}

export interface DiffValues<T> {
  added: T
  removed: T
}

export interface VehicleAppDiff {
  vehicles: VehiclesDiff
  deletedVehicles: VehiclesDiff
}

export interface DriverAppDiff {
  drivers: NamedDriversDiff
  deletedDrivers: NamedDriversDiff
}

export interface PlateAppDiff {
  plates: PlatesDiff
  deletedPlates: PlatesDiff
}

export interface DiffStore {
  diffData: Ref<DiffData | null>
  formDataDiffData: Ref<FormDataDiffData | null>
  loadedID: Ref<number | null>
  targetID: Ref<number | null>
  loading: Ref<boolean>
  showSummary: Ref<boolean>
  appDiffs: Ref<{
    namedDriversDiff: NamedDriversDiff | null
    driverDiff: DriverAppDiff | Record<string, DriverAppDiff> | null
    vehicleDiff: VehicleAppDiff | Record<string, VehicleAppDiff> | null
    plateDiff: PlateAppDiff | Record<string, PlateAppDiff> | null
    vehicleDiffEnglish: string | null
  }>
  vehicleDiffEnglish: ComputedRef<string | null>
  diffMap: ComputedRef<DiffMap | null>
  /**
   * Transforms removed diffs into a NestedResponseObject so that we can combine arrays more easily, `k` is used to find where they should exist.
   *
   * Different removed array items are guaranteed to have different indexes because the keys used indicate their index on the Comparison App, not the Base.
   *
   * We also do some post-processing on this to remove invalid diffs.
   */
  nestedRemovedDiffs: ComputedRef<NestedResponseObject | null>
  /**
   * Compare two Policies and set diffData to show changes
   */
  compare (baseID: number | null, newTargetID: number | null): Promise<void>
  resetExceptSummary: () => void
  $reset: () => void
}

export const useDiffStore = defineStore('diff', (): DiffStore => {
  const diffData: DiffStore['diffData'] = ref(null)
  const formDataDiffData: DiffStore['formDataDiffData'] = ref(null)
  const loadedID: DiffStore['loadedID'] = ref(null)
  const targetID: DiffStore['targetID'] = ref(null)
  const loading: DiffStore['loading'] = ref(false)
  const showSummary: DiffStore['showSummary'] = ref(false)
  const appDiffs: DiffStore['appDiffs'] = ref({
    namedDriversDiff: null,
    driverDiff: null,
    vehicleDiff: null,
    plateDiff: null,
    vehicleDiffEnglish: null,
  })

  const vehicleDiffEnglish: DiffStore['vehicleDiffEnglish'] = computed(() => appDiffs.value.vehicleDiffEnglish)

  const diffMap: DiffStore['diffMap'] = computed(() => {
    if (!diffData.value) return null
    const UUIDmap: DiffMap = {
      added: {},
      changed: {},
      removed: {},
    }
    for (const el of diffData.value.added) {
      // we only really need the v in the added diffMap but we include the whole object for consistency
      UUIDmap.added[el.uuid4] = el
    }
    const arrayPathRegex = /_\d+_/
    for (const el of diffData.value.changed) {
      const { k, v } = el.changed
      const [k1, k2] = k
      const arrName1 = k1.split(arrayPathRegex)[0]
      const arrName2 = k2.split(arrayPathRegex)[0]
      if (arrName1 !== arrName2) {
        // this has moved from one array to another, that's an equivalent of an add and a remove
        UUIDmap.removed[el.uuid4] = { uuid4: el.uuid4, k: k[0], v: v[0] }
        UUIDmap.added[el.uuid4] = { uuid4: el.uuid4, k: k[1], v: v[1] }
      }
      // even if this has changed array we still record v changes in changed
      // synced arrays with a missing counterpart are then able to show a change because the add/remove above is then ignored
      if (v[0] !== v[1]) {
        UUIDmap.changed[el.uuid4] = { uuid4: el.uuid4, v }
      }
    }
    for (const el of diffData.value.removed) {
      // we need k in the removed diffMap for combining removed items in fields so we include the whole object
      UUIDmap.removed[el.uuid4] = el
    }
    return UUIDmap
  })

  const nestedRemovedDiffs: DiffStore['nestedRemovedDiffs'] = computed(() => {
    if (!diffMap.value) return null
    const nestedDiffs: NestedResponseObject = {}
    Object.values(diffMap.value.removed).forEach((el) => {
      set(nestedDiffs, normalizePath(el.k), el)
    })
    removeInvalidDeletedDiffs(nestedDiffs)
    return nestedDiffs
  })

  const compare: DiffStore['compare'] = async (baseID, newTargetID) => {
    const apiStore = useApiStore()

    if (!baseID || !newTargetID) {
      resetExceptSummary()
      return
    }
    if (loadedID.value !== baseID) resetExceptSummary()
    if (targetID.value === newTargetID) return

    loadedID.value = baseID
    targetID.value = newTargetID
    loading.value = true

    try {
      const { policyState, formData } = await apiStore.application.compare(baseID, newTargetID)
      diffData.value = policyState
      formDataDiffData.value = formData
    } catch (err) {
      console.error(err)
      resetExceptSummary()
      throw err
    } finally {
      loading.value = false
    }
  }

  const resetExceptSummary: DiffStore['resetExceptSummary'] = () => {
    const summary = showSummary.value
    $reset()
    showSummary.value = summary
  }

  const $reset: DiffStore['$reset'] = () => {
    diffData.value = null
    formDataDiffData.value = null
    loadedID.value = null
    targetID.value = null
    loading.value = false
    showSummary.value = false
    appDiffs.value = {
      namedDriversDiff: null,
      driverDiff: null,
      vehicleDiff: null,
      plateDiff: null,
      vehicleDiffEnglish: null,
    }
  }

  return {
    diffData,
    formDataDiffData,
    loadedID,
    targetID,
    loading,
    showSummary,
    appDiffs,
    vehicleDiffEnglish,
    diffMap,
    nestedRemovedDiffs,
    compare,
    resetExceptSummary,
    $reset,
  }
})

hotReloadStore(useDiffStore)
